import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getLeaveRequestDetails, updateLeaveRequest, getEmployees, getLeaveTypes, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const normalizeListResponse = (payload) => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  // Normalize pagination to ensure it has current_page and last_page
  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};

const EditLeaveRequest = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const { id } = useParams();
  const { leaveRequestDetails, leaveTypes, isLoading, error, success } = useSelector((state) => state.auth);

  // Employee dropdown state
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeePagination, setEmployeePagination] = useState(null);
  const employeeSearchInputRef = useRef(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    leave_type_id: '',
    start_date: '',
    end_date: '',
    reason: ''
  });
  const lastErrorRef = useRef({ message: null, time: 0 });

  const fetchEmployees = useCallback(
    async (page = employeePage, search = employeeSearchTerm) => {
      try {
        const payload = await dispatch(
          getEmployees({
            page,
            per_page: 10,
            search: search || undefined,
            language: i18n.language,
          })
        ).unwrap();
        const { items, pagination: paginationMeta } = normalizeListResponse(payload);
        setEmployeeOptions(items);
        setEmployeePagination(paginationMeta);
      } catch (error) {
        toast.error(error || t('leaveRequests.edit.messages.fetchEmployeesError', { defaultValue: 'فشل جلب الموظفين' }), { rtl: isRTL });
      }
    },
    [dispatch, t, i18n, isRTL]
  );

  useEffect(() => {
    if (id) {
      dispatch(getLeaveRequestDetails(id));
    }
    dispatch(getLeaveTypes());
  }, [id, dispatch]);

  useEffect(() => {
    if (employeeDropdownOpen) {
      fetchEmployees(employeePage, employeeSearchTerm);
    }
  }, [employeeDropdownOpen, employeePage, fetchEmployees]);

  useEffect(() => {
    if (employeeSearchTerm !== undefined && employeeDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setEmployeePage(1);
        fetchEmployees(1, employeeSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [employeeSearchTerm, employeeDropdownOpen, fetchEmployees]);

  useEffect(() => {
    if (leaveRequestDetails) {
      const employeeId = leaveRequestDetails.employee?.id || '';
      setFormData({
        employee_id: employeeId,
        leave_type_id: leaveRequestDetails.leave_type?.id || '',
        start_date: leaveRequestDetails.start_date ? leaveRequestDetails.start_date.split('T')[0] : '',
        end_date: leaveRequestDetails.end_date ? leaveRequestDetails.end_date.split('T')[0] : '',
        reason: leaveRequestDetails.reason || ''
      });

      // Add selected employee to options if available
      if (employeeId && leaveRequestDetails.employee) {
        const employeeData = leaveRequestDetails.employee;
        setEmployeeOptions([{
          id: employeeData.id,
          name: employeeData.name,
          name_en: employeeData.name_en,
          name_ar: employeeData.name_ar,
          employee_code: employeeData.employee_code
        }]);
      }
    }
  }, [leaveRequestDetails]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideDropdown = event.target.closest('[data-dropdown-container]');
      const isClickOnDropdownToggle = event.target.closest('[data-dropdown-toggle]');

      if (!isClickInsideDropdown && !isClickOnDropdownToggle) {
        setEmployeeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
      navigate('/leave-requests');
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, navigate, dispatch, isRTL]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.leave_type_id || !formData.start_date || !formData.end_date) {
      toast.error(t('leaveRequests.edit.validation.requiredFields'), { rtl: isRTL });
      return;
    }

    // Validate dates
    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);

    if (startDate >= endDate) {
      toast.error(t('leaveRequests.edit.validation.invalidDates'), { rtl: isRTL });
      return;
    }

    await dispatch(updateLeaveRequest({ id, leaveRequestData: formData }));
  };

  const handleCancel = () => {
    navigate('/leave-requests');
  };

  if (isLoading && !leaveRequestDetails) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        color: 'white',
        direction: dir
      }}>
        <div>{t('leaveRequests.loading')}</div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir
    }}>
      {/* العنوان */}
      <div style={{ marginBottom: '30px' }}>
        <button
          onClick={() => navigate('/leave-requests')}
          style={{
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            padding: '10px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}
        >
          <FaArrowLeft />
          {t('leaveRequests.edit.back')}
        </button>

        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'white',
          margin: 0,
          textAlign
        }}>
          {t('leaveRequests.edit.title')}
        </h1>
      </div>


      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '30px',
        border: '1px solid #333',
        overflow: 'visible'
      }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>

            <div style={{ flex: '1', minWidth: '300px' }}>
              {/* الموظف */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('leaveRequests.edit.fields.employee')}
                </label>
                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div
                    data-dropdown-toggle
                    onClick={() => setEmployeeDropdownOpen((prev) => !prev)}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: dir,
                      textAlign,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <span>
                      {formData.employee_id
                        ? (() => {
                          const selected = employeeOptions.find((e) => String(e.id) === String(formData.employee_id));
                          return selected
                            ? `${(isRTL ? selected.name_ar : selected.name_en) || selected.name} - ${selected.employee_code}`
                            : t('leaveRequests.edit.placeholders.selectEmployee');
                        })()
                        : t('leaveRequests.edit.placeholders.selectEmployee')}
                    </span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  <DropdownWithSearch
                    isOpen={employeeDropdownOpen}
                    searchTerm={employeeSearchTerm}
                    onSearchChange={setEmployeeSearchTerm}
                    searchInputRef={employeeSearchInputRef}
                    items={employeeOptions}
                    pagination={employeePagination}
                    onPageChange={setEmployeePage}
                    onClose={() => setEmployeeDropdownOpen(false)}
                    onSelect={(item) => {
                      setFormData(prev => ({ ...prev, employee_id: String(item.id) }));
                      setEmployeeDropdownOpen(false);
                      setEmployeeSearchTerm('');
                      setEmployeePage(1);
                    }}
                    label={t('leaveRequests.edit.fields.employee')}
                    isRTL={isRTL}
                    i18n={i18n}
                  />
                </div>
              </div>


              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('leaveRequests.edit.fields.leaveType')}
                </label>
                <select
                  name="leave_type_id"
                  value={formData.leave_type_id}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    direction: dir
                  }}
                >
                  <option value="" style={{ backgroundColor: '#1a1f2e' }}>{t('leaveRequests.edit.placeholders.selectLeaveType')}</option>
                  {leaveTypes?.map(leaveType => (
                    <option key={leaveType.id} value={leaveType.id} style={{ backgroundColor: '#1a1f2e' }}>
                      {(isRTL ? leaveType.name_ar : leaveType.name_en) || leaveType.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>


            <div style={{ flex: '1', minWidth: '300px' }}>
              {/* تاريخ البداية */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('leaveRequests.edit.fields.startDate')}
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    colorScheme: 'dark',
                    direction: dir
                  }}
                />
              </div>


              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('leaveRequests.edit.fields.endDate')}
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    colorScheme: 'dark',
                    direction: dir
                  }}
                />
              </div>
            </div>
          </div>


          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: 'white',
              fontWeight: '500',
              textAlign
            }}>
              {t('leaveRequests.edit.fields.reason')}
            </label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleInputChange}
              placeholder={t('leaveRequests.edit.placeholders.reason')}
              rows="4"
              style={{
                width: '100%',
                padding: '12px 15px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                resize: 'vertical',
                direction: dir,
                textAlign
              }}
            />
          </div>

          {/* أزرار الإجراء */}
          <div style={{
            display: 'flex',
            gap: '15px',
            marginTop: '30px',
            justifyContent: 'center',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? '#666' : '#AC2000',
                color: 'white',
                border: 'none',
                padding: '12px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isLoading ? 0.6 : 1,
                flexDirection: isRTL ? 'row' : 'row-reverse'
              }}
            >
              {isLoading ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid white',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  {t('leaveRequests.edit.buttons.saving')}
                </>
              ) : (
                <>
                  <FaSave />
                  {t('leaveRequests.edit.buttons.save')}
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleCancel}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '12px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              {t('leaveRequests.edit.buttons.cancel')}
            </button>
          </div>
        </form>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

const dropdownMenuStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  zIndex: 10000,
  padding: 12,
  boxShadow: '0 8px 16px rgba(0,0,0,0.5)',
  maxHeight: '550px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const dropdownSearchStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid #333',
  backgroundColor: '#14263b',
  color: 'white',
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: 'center',
  color: '#ccc',
  fontSize: 13,
};

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
  isRTL = true,
  i18n,
}) => {
  const { t } = useTranslation();
  const dropdownIsRTL = isRTL !== undefined ? isRTL : (i18n?.language === 'ar');

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  return (
    <div style={{ ...dropdownMenuStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={t('leaveRequests.edit.placeholders.searchEmployee')}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={{ ...dropdownSearchStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}
        />
      </div>
      {pagination && (pagination.last_page > 1 || (pagination.last_page === 1 && pagination.current_page === 1)) && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}>
              {t('leaveRequests.edit.pagination.page')} {pagination.current_page} {t('leaveRequests.edit.pagination.of')} {pagination.last_page}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }}
              disabled={pagination.current_page === 1}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ««
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }}
              disabled={pagination.current_page === 1}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ‹
            </button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onPageChange(page); }}
                  style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}
                >
                  {page}
                </button>
              ));
            })()}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }}
              disabled={pagination.current_page === pagination.last_page}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ›
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }}
              disabled={pagination.current_page === pagination.last_page}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              »»
            </button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: '400px', overflowY: 'auto', flex: 1 }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div style={{ ...dropdownEmptyStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}>
            {t('leaveRequests.edit.pagination.noResults')}
          </div>
        ) : (
          (Array.isArray(items) ? items : []).map((item) => (
            <div
              key={item.id}
              style={{
                padding: '10px 12px',
                borderTop: '1px solid #333',
                cursor: 'pointer',
                direction: dropdownIsRTL ? 'rtl' : 'ltr',
                textAlign: dropdownIsRTL ? 'right' : 'left'
              }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2a2a2a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {(dropdownIsRTL ? item.name_ar : item.name_en) || item.name} - {item.employee_code}
            </div>
          ))
        )}
        <div style={{ height: '40px' }}></div>
      </div>
    </div>
  );
};

export default EditLeaveRequest;
